import { useCallback, useEffect, useRef, useState } from "react";
import { startRealtimeCall, type RealtimeEvent, type RealtimeHandle } from "./realtimeClient";
import { buildAttackerPrompt, type StageStatus } from "./prompt";
import { detectEvents, looksLikeRefusal } from "./detector";
import { outcomeFromScoringEvent, useSim } from "../state/store";
import type { VoiceAttackerSpec } from "../scenario/types";

type PlaybookStage = NonNullable<NonNullable<VoiceAttackerSpec["playbook"]>["stages"]>[number];

/**
 * Lightweight client-side "slow brain" for the playbook ladder — the live
 * Realtime model (fast brain) is prompt-steered to work one stage at a time,
 * but nothing stops it from silently drifting (skipping ahead, repeating a
 * resolved stage, or dumping several stages into one turn) since the model
 * has no real memory of what it already asked. This tracker watches each
 * FINALIZED learner utterance, decides whether the CURRENT stage was
 * satisfied (via the same `detectEvents` hints already used for scoring) or
 * refused (a generic pushback heuristic), and on any change re-steers the
 * live session with a `session.update` carrying a fresh prompt that pins
 * down exactly which stage to work next — closer to the main product's
 * "slow brain re-steers the fast brain" pattern, adapted to this simpler
 * client (2026-07-26).
 */
class PlaybookStageTracker {
  private stages: PlaybookStage[];
  private persistence: number;
  private idx = 0;
  private statuses: Record<string, StageStatus> = {};
  private refusalCounts: Record<string, number> = {};

  constructor(attacker: VoiceAttackerSpec) {
    this.stages = attacker.playbook?.stages ?? [];
    this.persistence = attacker.playbook?.persistence ?? 2;
    for (const s of this.stages) this.statuses[s.id] = "pending";
  }

  /** Feed one finalized learner utterance + whatever scoring events it hit.
   *  Returns the new focus (for a `session.update`) IFF the stage advanced,
   *  or `null` if nothing changed (so the caller can skip the update). */
  observe(utterance: string, scoringHits: { id: string }[]): { stageId: string; statuses: Record<string, StageStatus> } | null {
    if (this.idx >= this.stages.length) return null;
    const stage = this.stages[this.idx];

    const satisfied = scoringHits.some(
      (h) => h.id === stage.satisfyEventId || stage.satisfyEventIds?.includes(h.id)
    );
    if (satisfied) {
      this.statuses[stage.id] = "satisfied";
      this.idx += 1;
      return this.focus();
    }

    if (looksLikeRefusal(utterance)) {
      this.refusalCounts[stage.id] = (this.refusalCounts[stage.id] ?? 0) + 1;
      if (this.refusalCounts[stage.id] >= this.persistence) {
        this.statuses[stage.id] = "refused";
        this.idx += 1;
        return this.focus();
      }
    }
    return null;
  }

  private focus(): { stageId: string; statuses: Record<string, StageStatus> } {
    const next = this.stages[this.idx];
    return { stageId: next?.id ?? "__ladder_complete__", statuses: { ...this.statuses } };
  }
}

export type CallStatus = "idle" | "connecting" | "in_call" | "ended" | "error";

/** ECHO GUARD tail (ch.11 pattern): keep the mic muted this long AFTER
 *  `response.done` before re-enabling it, to absorb WebRTC/audio-element
 *  playback jitter — the transcript event fires slightly before the tail end
 *  of the audio has actually finished coming out of the learner's speakers. */
const MIC_GATE_TAIL_MS = 500;

/** Voices the OpenAI Realtime API accepts. A persona's `voiceId` doubles as its
 *  scripted-TTS voice, where extra voices like "onyx" are valid — but the
 *  Realtime token mint 400s on anything outside this set, killing the whole
 *  call. Map unknown/TTS-only voices onto a sensible Realtime voice so a live
 *  call never fails just because of the voice name. */
const REALTIME_VOICES = new Set([
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse",
  "marin",
  "cedar",
]);
const REALTIME_VOICE_ALIASES: Record<string, string> = {
  onyx: "ash", // deep male TTS voice -> nearest serious male Realtime voice
  nova: "shimmer",
  fable: "verse",
};
function realtimeVoice(voiceId?: string): string | undefined {
  if (!voiceId) return undefined;
  if (REALTIME_VOICES.has(voiceId)) return voiceId;
  return REALTIME_VOICE_ALIASES[voiceId] ?? "ash";
}

/**
 * Orchestrates one live voice call: connects Realtime, streams both transcripts
 * into the store, scores learner utterances live, and enforces the cost guard.
 */
export function useVoiceCall(
  attacker: VoiceAttackerSpec,
  beatId: string,
  /** TYPED FALLBACK: when true, skip the mic — the learner types (see
   *  `sendText`) but still hears the attacker's voice. */
  typed = false
) {
  const [status, setStatus] = useState<CallStatus>("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Change/issue (b): the "Open <tool>" action starts greyed out and only
  // unlocks once the playbook ladder is exhausted and the model has been
  // steered toward the remote-tool escalation — i.e. the moment the caller
  // actually asks for it, not before.
  const [readyForRemoteAsk, setReadyForRemoteAsk] = useState(false);

  const handleRef = useRef<RealtimeHandle | null>(null);
  const timerRef = useRef<number | null>(null);
  const micGateTimerRef = useRef<number | null>(null);
  const attackerTextRef = useRef<Map<string, string>>(new Map());
  const learnerTextRef = useRef<Map<string, string>>(new Map());
  // Scored events are recorded at most once per call (per-call event semantics,
  // matching the Cyberfrog voice engine). Keeps the outcome log the single,
  // stable source of truth for the debrief/score.
  const recordedRef = useRef<Set<string>>(new Set());
  const stageTrackerRef = useRef<PlaybookStageTracker | null>(null);

  const push = useSim((s) => s.pushTranscript);
  const update = useSim((s) => s.updateTranscript);
  const record = useSim((s) => s.recordOutcome);
  const clearTranscript = useSim((s) => s.clearTranscript);

  /** Score a finalized learner utterance AND advance the playbook stage
   *  tracker off the exact same detector hits, re-steering the live model
   *  via `session.update` whenever the stage actually changes. Shared by
   *  both the voice path (transcribed speech) and the typed fallback. */
  const scoreAndSteer = useCallback(
    (text: string) => {
      const hits = detectEvents(text, attacker.scoringEvents);
      for (const ev of hits) {
        if (recordedRef.current.has(ev.id)) continue;
        recordedRef.current.add(ev.id);
        record(outcomeFromScoringEvent(beatId, ev, text));
      }
      const focus = stageTrackerRef.current?.observe(text, hits) ?? null;
      if (focus && handleRef.current) {
        handleRef.current.send({
          type: "session.update",
          session: { instructions: buildAttackerPrompt(attacker, focus) },
        });
        if (focus.stageId === "__ladder_complete__") setReadyForRemoteAsk(true);
      }
    },
    [attacker, beatId, record]
  );

  const handleEvent = useCallback(
    (e: RealtimeEvent) => {
      switch (e.type) {
        // Attacker (model) speech transcript
        case "response.created": {
          const id = (e as any).response?.id ?? `att_${Date.now()}`;
          attackerTextRef.current.set(id, "");
          push({ id: `att_${id}`, role: "attacker", text: "", final: false });
          // ECHO GUARD ON: the caller is about to start talking — mute the
          // mic for the whole turn so their own voice coming back out of the
          // learner's speakers can't leak into the mic and false-trigger
          // server-VAD, which otherwise cancels the in-progress response and
          // starts a new one — the exact "gets cut off when a new text
          // appears" bug report (2026-07-26). Cancel any pending re-enable
          // from a previous turn first.
          if (micGateTimerRef.current) {
            window.clearTimeout(micGateTimerRef.current);
            micGateTimerRef.current = null;
          }
          handleRef.current?.setMicEnabled(false);
          break;
        }
        // Fires once the caller's whole turn (audio + transcript) is done.
        // Re-open the mic after a short tail so trailing playback jitter
        // can't still leak in as the gate lifts.
        case "response.done": {
          if (micGateTimerRef.current) window.clearTimeout(micGateTimerRef.current);
          micGateTimerRef.current = window.setTimeout(() => {
            handleRef.current?.setMicEnabled(true);
            micGateTimerRef.current = null;
          }, MIC_GATE_TAIL_MS);
          break;
        }
        // GA event name is `response.output_audio_transcript.*`; the pre-GA
        // `response.audio_transcript.*` is kept as a fallback for older models.
        case "response.output_audio_transcript.delta":
        case "response.audio_transcript.delta": {
          const id = (e as any).response_id ?? lastKey(attackerTextRef.current);
          if (id == null) break;
          const cur = (attackerTextRef.current.get(id) ?? "") + ((e as any).delta ?? "");
          attackerTextRef.current.set(id, cur);
          update(`att_${id}`, cur, false);
          break;
        }
        case "response.output_audio_transcript.done":
        case "response.audio_transcript.done": {
          const id = (e as any).response_id ?? lastKey(attackerTextRef.current);
          if (id == null) break;
          update(`att_${id}`, (e as any).transcript ?? attackerTextRef.current.get(id) ?? "", true);
          break;
        }

        // Learner (user) speech transcription
        case "input_audio_buffer.speech_started": {
          const id = `usr_${Date.now()}`;
          learnerTextRef.current.set(id, "");
          push({ id, role: "learner", text: "\u2026", final: false });
          break;
        }
        case "conversation.item.input_audio_transcription.delta": {
          const id = (e as any).item_id ?? lastKey(learnerTextRef.current);
          if (id == null) break;
          const cur = (learnerTextRef.current.get(id) ?? "") + ((e as any).delta ?? "");
          learnerTextRef.current.set(id, cur);
          update(id.startsWith("usr_") ? id : `usr_${id}`, cur, false);
          break;
        }
        case "conversation.item.input_audio_transcription.completed": {
          const itemId = (e as any).item_id ?? "";
          const text: string = (e as any).transcript ?? learnerTextRef.current.get(itemId) ?? "";
          const lineId = itemId.startsWith("usr_") ? itemId : `usr_${itemId}`;
          // Surface + score ONLY the finalized learner utterance (never the live
          // tentative transcript). Each scored event is logged once per call.
          push({ id: `${lineId}_final`, role: "learner", text, final: true });
          scoreAndSteer(text);
          break;
        }
        default:
          break;
      }
    },
    [push, scoreAndSteer, update]
  );

  const start = useCallback(async () => {
    setStatus("connecting");
    setError(null);
    // `transcript` is a single global array shared by every call-style
    // device in the course (VideoCall/PhoneCall/TypedCall/DeepfakeCall/this
    // hook) — wipe it here so an earlier call's lines (e.g. the Teams
    // deepfake call finished episodes ago) never bleed into this call's chat
    // log (2026-07-26 regression).
    clearTranscript();
    recordedRef.current.clear();
    attackerTextRef.current.clear();
    learnerTextRef.current.clear();
    stageTrackerRef.current = new PlaybookStageTracker(attacker);
    record({ type: "call_answered", beatId });
    try {
      const handle = await startRealtimeCall(
        {
          instructions: buildAttackerPrompt(attacker),
          voice: realtimeVoice(attacker.persona.voiceId),
          language: attacker.persona.language,
          mic: !typed,
        },
        handleEvent
      );
      handleRef.current = handle;
      setStatus("in_call");
      timerRef.current = window.setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (attacker.maxDurationS && next >= attacker.maxDurationS) {
            hangup();
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attacker, beatId, handleEvent, record, typed]);

  /** TYPED FALLBACK: submit a learner text turn. Mirrors the voice path —
   *  push the line into the transcript, score it, then hand it to the model. */
  const sendText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !handleRef.current) return;
      push({ id: `usr_typed_${Date.now()}`, role: "learner", text: trimmed, final: true });
      scoreAndSteer(trimmed);
      handleRef.current.sendText(trimmed);
    },
    [push, scoreAndSteer]
  );

  const hangup = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    if (micGateTimerRef.current) window.clearTimeout(micGateTimerRef.current);
    handleRef.current?.stop();
    handleRef.current = null;
    setStatus("ended");
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (micGateTimerRef.current) window.clearTimeout(micGateTimerRef.current);
      handleRef.current?.stop();
    };
  }, []);

  return { status, seconds, error, start, hangup, sendText, readyForRemoteAsk };
}

function lastKey(m: Map<string, string>): string | null {
  let last: string | null = null;
  for (const k of m.keys()) last = k;
  return last;
}
